import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { languages } from "../../../constants";
import UserService from "@services/UserService";
import User from "@ltypes/user";
import Card from "@components/ui/Card/Card";
import TextField from "@components/forms/TextField/TextField";
import Select from "@components/forms/Select/Select";
import Button from "@components/forms/Button/Button";
import { updateUser } from "@slices/userSlice";

type FormData = {
    settings: {
        name?: string,
        email?: string,
        language?: string,
    },
    password: {
        current_password?: string,
        new_password?: string,
        confirmation_password?: string,
    },
};
type FormDataCategory = 'settings'|'password';

function AccountPage() {
    const [formData, setFormData] = useState<FormData>({
        settings: {},
        password: {},
    });
    
    const user: User|null = useSelector((state: any) => state.user);
    const dispatch = useDispatch();

    const { t, i18n } = useTranslation();

    const service = new UserService;

    const handleChange = (category: FormDataCategory, key: string, value: any) => {
        setFormData({
            ...formData,
            [category]: {
                ...formData[category],
                [key]: value,
            },
        });
    };

    const handleSave = () => {
        const promise = service.update({
            ...formData.settings,
            ...(formData.password.current_password && formData.password.new_password && formData.password.confirmation_password ? formData.password : {}),
        });

        toast.promise(promise, {
            loading: t('toast.updating'),
            success: () => t('toast.updated'),
            error: err => err.body.message || t('toast.error_fetching'),
        });

        promise.then(data => {
            dispatch(updateUser(data.body));
            if(formData.settings.language) {
                i18n.changeLanguage(formData.settings.language);
            }
            resetFormData();
        }).catch(e => {
        });
    };
    
    const resetFormData = () => {
        setFormData({
            settings: {},
            password: {},
        });
    };

    return (
        <>
            <div className="p-3">
                <h1>{t('title.account_settings')}</h1>

                <Card>
                    <div className="row g-3">
                        <div className="col-sm-12 col-md-6">
                            <div className="row g-3">
                                <div className="col-12">
                                    <TextField label={t('page.account.name')} value={formData.settings.name ?? user?.name ?? ''} onChange={(value: string) => handleChange('settings', 'name', value)}/>
                                </div>
                                <div className="col-12">
                                    <TextField disabled label={t('page.account.email')} value={formData.settings.email ?? user?.email ?? ''} onChange={(value: string) => handleChange('settings', 'email', value)}/>
                                </div>
                            </div>
                        </div>

                        <div className="col-sm-12 col-md-6">
                            <Select label={t('page.account.language')} value={formData.settings.language ?? user?.language ?? 'en'} items={languages} onChange={(value: string) => handleChange('settings', 'language', value)}/>
                        </div>
                    </div>

                    <div className="row mt-3 gx-2 justify-content-end">
                        <div className="col-auto">
                            <Button disabled={Object.keys(formData.settings).length === 0} className="secondary" onClick={resetFormData}>{t('text.cancel')}</Button>
                        </div>
                        <div className="col-auto">
                            <Button disabled={Object.keys(formData.settings).length === 0} onClick={handleSave}>{t('text.save')}</Button>
                        </div>
                    </div>
                </Card>
            </div>

            <div className="p-3">
                <h1>{t('title.account_password')}</h1>

                <Card>
                    <div className="row g-3">
                        <div className="col-12">
                            <div className="row g-3">
                                <div className="col-sm-12 col-md-6">
                                    <TextField type="password" label={t('page.account.current_password')} value={formData.password.current_password ?? ''} onChange={(value: string) => handleChange('password', 'current_password', value)} autocomplete="off"/>
                                </div>
                            </div>
                            
                        </div>

                        <div className="col-12">
                            <div className="row g-3">
                                <div className="col-sm-12 col-md-6">
                                    <TextField type="password" label={t('page.account.new_password')} value={formData.password.new_password ?? ''} onChange={(value: string) => handleChange('password', 'new_password', value)} autocomplete="off"/>
                                </div>
                                <div className="col-sm-12 col-md-6">
                                    <TextField type="password" label={t('page.account.confirmation_password')} value={formData.password.confirmation_password ?? ''} onChange={(value: string) => handleChange('password', 'confirmation_password', value)} autocomplete="off"/>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="row mt-3 gx-2 justify-content-end">
                        <div className="col-auto">
                            <Button disabled={Object.keys(formData.password).length === 0} className="secondary" onClick={resetFormData}>{t('text.cancel')}</Button>
                        </div>
                        <div className="col-auto">
                            <Button disabled={Object.keys(formData.password).length === 0} onClick={handleSave}>{t('text.save')}</Button>
                        </div>
                    </div>
                </Card>
            </div>
        </>
    );
}

export default AccountPage;
